import { useEffect, useRef, type RefObject } from "react";
import { safeTauriUnlisten } from "@/desktop/tauri-listener-cleanup";
import { isTauri } from "@/lib/is-tauri";

export interface NativeFileDropHandlers {
  /** A drag moved (true when over the root) or left the window (false). */
  onHover?: (over: boolean) => void;
  /**
   * The OS paths dropped over the root; `alive()` turns false once the
   * listener is gone (unmounted or disabled), for reads that finish later.
   */
  onDrop: (paths: string[], alive: () => boolean) => unknown;
}

/** What Tauri sends with a drag event; a leave sends nothing. */
interface DragPayload {
  paths?: string[];
  /** Physical pixels in the webview. */
  position?: { x: number; y: number };
}

/** The webview's drag events (Tauri's `TauriEvent.DRAG_*`), each with
 *  whether it is the drop. */
const DRAG_EVENTS = [
  ["tauri://drag-enter", false],
  ["tauri://drag-over", false],
  ["tauri://drag-drop", true],
  ["tauri://drag-leave", false],
] as const;

/**
 * The desktop app's OS file drops onto `rootRef`. Tauri consumes them before
 * any DOM drag event and tells every listener in the window, so each
 * surface (the composer, the wall) claims the drops whose point lies inside
 * its own root, hit-tested at the event's physical position. Nothing on the
 * web, or while `enabled` is false.
 *
 * Each drag event is its own listener, removed with `safeTauriUnlisten`: an
 * unmount right after subscribing (before Tauri installed the listener)
 * neither rejects unhandled nor leaves a listener Tauri keeps calling. A
 * delivery after unmount is ignored.
 */
export function useNativeFileDrop(
  rootRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  handlers: NativeFileDropHandlers,
): void {
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    if (!isTauri() || !enabled) return;
    let active = true;
    const alive = () => active;
    const unlistens: Array<() => void> = [];

    const onDrag = (isDrop: boolean, payload: DragPayload | null) => {
      if (!active) return;
      const position = payload?.position;
      if (!position) {
        latest.current.onHover?.(false);
        return;
      }
      const ratio = window.devicePixelRatio || 1;
      const target = document.elementFromPoint(
        position.x / ratio,
        position.y / ratio,
      );
      const over = Boolean(target && rootRef.current?.contains(target));
      latest.current.onHover?.(over && !isDrop);
      if (isDrop && over) latest.current.onDrop(payload.paths ?? [], alive);
    };

    void import("@tauri-apps/api/webview").then(
      ({ getCurrentWebview }) => {
        if (!active) return;
        const webview = getCurrentWebview();
        for (const [event, isDrop] of DRAG_EVENTS) {
          webview
            .listen<DragPayload | null>(event, ({ payload }) =>
              onDrag(isDrop, payload),
            )
            .then(
              (unlisten) => {
                if (active) unlistens.push(unlisten);
                else safeTauriUnlisten(unlisten);
              },
              () => {},
            );
        }
      },
      () => {},
    );
    return () => {
      active = false;
      for (const unlisten of unlistens.splice(0)) safeTauriUnlisten(unlisten);
    };
  }, [rootRef, enabled]);
}
